import AmarelinhoService from "../services/AmarelinhoService";
import { UtmService } from "../services/UtmService";
import { CourseDto, CourseTypeDto } from './dto/course.dto';
import { DeficiencyDto } from './dto/deficiency.dto';
import { ExperienceDto } from './dto/experience.dto';
import { LanguageDto, LanguageLevelDto, LanguageNameDto } from './dto/language.dto';
import { NormalizedRoleDto, RoleDto } from './dto/role.dto';
import { SalaryExpectationDto } from './dto/salary-expectation.dto';
import { SchoolingDto, SchoolingLevel } from './dto/schooling.dto';

export interface SignupModel {
  name: string;
  gender: string;
  birthdate: Date;
  phone: string;
  email: string;
  password: string;
  document: string;
  maritalStatus: { description: string, value: string };
  nationality: string;
  deficiency?: DeficiencyDto | undefined | null;
  address: AddressModel;
  objectives: JobObjectiveModel[];
  graduations: GraduationModel[];
  experiences: JobExperiencesModel[];
  courses: CourseModel[];
  languages: LanguageModel[];
  presentationLetter: PresentationLetterModel;
  driverQuestions: DriverQuestionsModel[];
  dressmakerQuestions: DressmakerQuestionsModel[];
  manicureQuestions: ManicureQuestionsModel[];
  painterQuestions: PainterQuestionsModel[];
  sellerQuestions: SellerQuestionsModel[];
  salaryExpectation: SalaryExpectationDto;
  cvExtraData?: CvExtraDataModel;
}

export interface PresentationLetterModel {
  description: string;
  saved: boolean;
}

export interface CvExtraDataModel {
  hasDriverLicense: boolean | null;
  driverLicenseCategory: string | null;
  hasMotorcycle: boolean | null;
  hasMotorcycleCargoBox: boolean | null;
  immediateStartAvailability: boolean | null;
  weekendWorkAvailability: boolean | null;
}

export interface DriverQuestionsModel {
  workAtNight: string;
  workWeekend: string;
  license: string;
  vehicle: string;
  vehicleTrunk: string;
  vehicleCargo: string;
  vehicleMopp: string;
  vehicleYear: number;
  saved: boolean;
}

export interface DressmakerQuestionsModel {
  machine: string;
  cloth: string;
  saved: boolean;
}

export interface ManicureQuestionsModel {
  ownMaterial: string;
  hasCompanyOpen: string;
  nailTypes: string;
  saved: boolean;
}

export interface PainterQuestionsModel {
  technique: string;
  saved: boolean;
}

export interface SellerQuestionsModel {
  saleType: string;
  saved: boolean;
}

export interface JobExperiencesModel {
  position: string;
  companyName: string;
  current: boolean;
  start: Date;
  finish: Date;
  activities: string;
  saved: boolean;
}

export const CV_EXTRA_DATA_EXPERIENCE_KEY = '_#CV_EXTRA_DATA#_';

export interface AddressModel {
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface GraduationModel {
  type: GraduationType;
  name: string;
  course: string;
  current: boolean;
  start: Date;
  finish: Date;
  saved: boolean;
}


export interface LanguageModel {
  type: LanguageType;
  level: LanguageLevel;
  otherType: string;
  saved: boolean;
}

export enum LanguageType {
  INGLES,
  ESPANHOL,
  FRANCES,
  ALEMAO,
  OUTRO
}

export enum LanguageLevel {
  BASICO,
  INTERMEDIARIO,
  FLUENTE,
  NATIVO
}

export interface CourseModel {
  type: CourseType;
  typeOther: string;
  name: string;
  course: string;
  current: boolean;
  start: Date;
  finish: Date;
  saved: boolean;
}

export enum CourseType {
  APRENDIZAGEM,
  CONCURSO,
  CURSO_LIVRE,
  GRADUACAO_E_POS,
  IDIOMA,
  TECNICO,
  OUTRO
}

export enum GraduationType {
  SEM_FORMACAO = 9,
  FUNDAMENTAL_INCOMPLETO = 7,
  FUNDAMENTAL_COMPLETO = 1,
  MEDIO_INCOMPLETO = 2,
  MEDIO_COMPLETO = 3,
  SUPERIOR_INCOMPLETO = 4,
  SUPERIOR_COMPLETO = 5,
  TECNICO = 8,
  POS_GRADUACAO = 6
}

export function toSchoolingLevel(input: GraduationType): SchoolingLevel {
  switch (input) {
    case GraduationType.FUNDAMENTAL_COMPLETO:
      return 'Ensino Fundamental Completo';
    case GraduationType.MEDIO_INCOMPLETO:
      return 'Ensino Médio Incompleto';
    case GraduationType.MEDIO_COMPLETO:
      return 'Ensino Médio Completo';
    case GraduationType.SUPERIOR_INCOMPLETO:
      return 'Ensino Superior Incompleto';
    case GraduationType.SUPERIOR_COMPLETO:
      return 'Ensino Superior Completo';
    case GraduationType.POS_GRADUACAO:
      return 'Pós-Graduação';
    default:
      return 'Ensino Fundamental Incompleto';
  }
}


export function getCourseType(type: CourseType): CourseTypeDto {
  switch (type) {
    case CourseType.APRENDIZAGEM: return "Aprendizagem";
    case CourseType.CONCURSO: return "Concursos";
    case CourseType.CURSO_LIVRE: return "Cursos Livres";
    case CourseType.IDIOMA: return "Idiomas";
    case CourseType.TECNICO: return "Técnico";
    default: return "Outros";
  }
}

export interface JobObjectiveModel {
  id: string;
  value: string;
  label: string;
}

export class JobObjectiveHelper {
  static fromList(l: RoleDto[]) {
    if (!l) return [];
    return l.map((item) => {
      return {
        id: String(item.id),
        value: item.slug,
        label: item.description
      } as JobObjectiveModel;
    });
  }

  static fromNormalizedRoles(l: NormalizedRoleDto[]) {
    if (!l) return [];
    return l.map((item) => {
      return {
        id: String(item.codigo),
        value: item.nome,
        label: item.nome
      } as JobObjectiveModel;
    });
  }
}

export class SignupModelHelper {

  static getUserID(): String | null {
    let userId = localStorage.getItem('userId') || null;
    return userId;
  }

  static async checkLogin(force: boolean = false): Promise<number | null> {
    let userId = localStorage.getItem('userId');
    if (force || !userId) {
      const req = await AmarelinhoService.isLogado();
      if (req.success) {
        localStorage.setItem('userId', req.userId);
        userId = req.userId;
      } else {
        userId = null;
      }
    }
    return userId ? +userId : null;
  }

  static reset() {
    localStorage.setItem('signup', JSON.stringify(''));
  }

  static update(obj: Partial<SignupModel>) {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);
      signupModel = { ...signupModel, ...obj };
    } else {
      signupModel = {
        ...obj
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
  }

  static getOngoingExperience(): JobExperiencesModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.experiences) {
        const latestExperience = signupModel.experiences.filter(experience => experience.saved === false);
        if (latestExperience.length === 1) {
          return latestExperience[0];
        }
      }
    }
    return {
      saved: false,
    } as JobExperiencesModel;
  }

  static getOngoingGraduation(): GraduationModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.graduations) {
        const latestGraduation = signupModel.graduations.filter(graduation => graduation.saved === false);
        if (latestGraduation.length === 1) {
          return latestGraduation[0];
        }
      }
    }
    return {
      saved: false,
    } as GraduationModel;
  }

  static getOngoingCourse(): CourseModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.courses) {
        const latestCourse = signupModel.courses.filter(course => course.saved === false);
        if (latestCourse.length === 1) {
          return latestCourse[0];
        }
      }
    }
    return {
      saved: false,
    } as CourseModel;
  }

  static getOngoingLanguage(): LanguageModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.languages) {
        const latestLanguage = signupModel.languages.filter(course => course.saved === false);
        if (latestLanguage.length === 1) {
          return latestLanguage[0];
        }
      }
    }
    return {
      saved: false,
    } as LanguageModel;
  }

  static getOngoingDriverQuestions(): DriverQuestionsModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.driverQuestions) {
        const latestDriverQuestion = signupModel.driverQuestions.filter(driverQuestion => driverQuestion.saved === false);
        if (latestDriverQuestion.length === 1) {
          return latestDriverQuestion[0];
        }
      }
    }
    return {
      saved: false,
    } as DriverQuestionsModel;
  }

  static getOngoingDressmakerQuestions(): DressmakerQuestionsModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.dressmakerQuestions) {
        const latestQuestion = signupModel.dressmakerQuestions.filter(dressmakerQuestion => dressmakerQuestion.saved === false);
        if (latestQuestion.length === 1) {
          return latestQuestion[0];
        }
      }
    }
    return {
      saved: false,
    } as DressmakerQuestionsModel;
  }

  static getOngoingManicureQuestions(): ManicureQuestionsModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.manicureQuestions) {
        const latestQuestion = signupModel.manicureQuestions.filter(manicureQuestion => manicureQuestion.saved === false);
        if (latestQuestion.length === 1) {
          return latestQuestion[0];
        }
      }
    }
    return {
      saved: false,
    } as ManicureQuestionsModel;
  }

  static getOngoingPainterQuestions(): PainterQuestionsModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.painterQuestions) {
        const latestQuestion = signupModel.painterQuestions.filter(painterQuestion => painterQuestion.saved === false);
        if (latestQuestion.length === 1) {
          return latestQuestion[0];
        }
      }
    }
    return {
      saved: false,
    } as PainterQuestionsModel;
  }

  static getOngoingSellerQuestions(): SellerQuestionsModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.sellerQuestions) {
        const latestQuestion = signupModel.sellerQuestions.filter(sellerQuestion => sellerQuestion.saved === false);
        if (latestQuestion.length === 1) {
          return latestQuestion[0];
        }
      }
    }
    return {
      saved: false,
    } as SellerQuestionsModel;
  }

  static updateExperience(experiencePartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.experiences) {
        const latestExperience = signupModel.experiences.filter(experience => experience.saved === false);
        if (latestExperience.length === 1) {
          let idx = signupModel.experiences.indexOf(latestExperience[0]);
          signupModel.experiences[idx] = {
            ...signupModel.experiences[idx],
            ...experiencePartialData
          };
        } else {
          signupModel.experiences.push({
            saved: false,
            ...experiencePartialData,
          });
        }

      } else {
        signupModel.experiences = [{
          saved: false,
          ...experiencePartialData,
        }]
      }
    } else {
      var experience = {
        saved: false,
        ...experiencePartialData,
      } as JobExperiencesModel;
      signupModel = {
        experiences: [experience]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updateGraduation(graduationPartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.graduations) {
        const latestGraduation = signupModel.graduations.filter(graduation => graduation.saved === false);
        if (latestGraduation.length === 1) {
          let idx = signupModel.graduations.indexOf(latestGraduation[0]);
          signupModel.graduations[idx] = {
            ...signupModel.graduations[idx],
            ...graduationPartialData
          };
        } else {
          signupModel.graduations.push({
            saved: false,
            ...graduationPartialData,
          });
        }

      } else {
        signupModel.graduations = [{
          saved: false,
          ...graduationPartialData,
        }]
      }
    } else {
      var graduation = {
        saved: false,
        ...graduationPartialData,
      } as GraduationModel;
      signupModel = {
        graduations: [graduation]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updateCourse(coursePartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.courses) {
        const latestCourse = signupModel.courses.filter(course => course.saved === false);
        if (latestCourse.length === 1) {
          let idx = signupModel.courses.indexOf(latestCourse[0]);
          signupModel.courses[idx] = {
            ...signupModel.courses[idx],
            ...coursePartialData
          };
        } else {
          signupModel.courses.push({
            saved: false,
            ...coursePartialData,
          });
        }

      } else {
        signupModel.courses = [{
          saved: false,
          ...coursePartialData,
        }]
      }
    } else {
      var course = {
        saved: false,
        ...coursePartialData,
      } as CourseModel;
      signupModel = {
        courses: [course]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updateLanguage(languagePartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.languages) {
        const latestLanguage = signupModel.languages.filter(language => language.saved === false);
        if (latestLanguage.length === 1) {
          let idx = signupModel.languages.indexOf(latestLanguage[0]);
          signupModel.languages[idx] = {
            ...signupModel.languages[idx],
            ...languagePartialData
          };
        } else {
          signupModel.languages.push({
            saved: false,
            ...languagePartialData,
          });
        }

      } else {
        signupModel.languages = [{
          saved: false,
          ...languagePartialData,
        }]
      }
    } else {
      var language = {
        saved: false,
        ...languagePartialData,
      } as LanguageModel;
      signupModel = {
        languages: [language]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updateDriverQuestions(driverPartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.driverQuestions) {
        const latestQuestion = signupModel.driverQuestions.filter(driverQuestion => driverQuestion.saved === false);
        if (latestQuestion.length === 1) {
          let idx = signupModel.driverQuestions.indexOf(latestQuestion[0]);
          signupModel.driverQuestions[idx] = {
            ...signupModel.driverQuestions[idx],
            ...driverPartialData
          };
        } else {
          signupModel.driverQuestions.push({
            saved: false,
            ...driverPartialData,
          });
        }

      } else {
        signupModel.driverQuestions = [{
          saved: false,
          ...driverPartialData,
        }]
      }
    } else {
      var driverQuestions = {
        saved: false,
        ...driverPartialData,
      } as DriverQuestionsModel;
      signupModel = {
        driverQuestions: [driverQuestions]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updateDressmakerQuestions(dressmakerPartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.dressmakerQuestions) {
        const latestQuestion = signupModel.dressmakerQuestions.filter(dressmakerQuestion => dressmakerQuestion.saved === false);
        if (latestQuestion.length === 1) {
          let idx = signupModel.dressmakerQuestions.indexOf(latestQuestion[0]);
          signupModel.dressmakerQuestions[idx] = {
            ...signupModel.dressmakerQuestions[idx],
            ...dressmakerPartialData
          };
        } else {
          signupModel.dressmakerQuestions.push({
            saved: false,
            ...dressmakerPartialData,
          });
        }

      } else {
        signupModel.dressmakerQuestions = [{
          saved: false,
          ...dressmakerPartialData,
        }]
      }
    } else {
      var dressmakerQuestions = {
        saved: false,
        ...dressmakerPartialData,
      } as DressmakerQuestionsModel;
      signupModel = {
        dressmakerQuestions: [dressmakerQuestions]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updateManicureQuestions(manicurePartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.manicureQuestions) {
        const latestQuestion = signupModel.manicureQuestions.filter(manicureQuestion => manicureQuestion.saved === false);
        if (latestQuestion.length === 1) {
          let idx = signupModel.manicureQuestions.indexOf(latestQuestion[0]);
          signupModel.manicureQuestions[idx] = {
            ...signupModel.manicureQuestions[idx],
            ...manicurePartialData
          };
        } else {
          signupModel.manicureQuestions.push({
            saved: false,
            ...manicurePartialData,
          });
        }

      } else {
        signupModel.manicureQuestions = [{
          saved: false,
          ...manicurePartialData,
        }]
      }
    } else {
      var manicureQuestions = {
        saved: false,
        ...manicurePartialData,
      } as ManicureQuestionsModel;
      signupModel = {
        manicureQuestions: [manicureQuestions]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updatePainterQuestions(painterPartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.painterQuestions) {
        const latestQuestion = signupModel.painterQuestions.filter(painterQuestion => painterQuestion.saved === false);
        if (latestQuestion.length === 1) {
          let idx = signupModel.painterQuestions.indexOf(latestQuestion[0]);
          signupModel.painterQuestions[idx] = {
            ...signupModel.painterQuestions[idx],
            ...painterPartialData
          };
        } else {
          signupModel.painterQuestions.push({
            saved: false,
            ...painterPartialData,
          });
        }

      } else {
        signupModel.painterQuestions = [{
          saved: false,
          ...painterPartialData,
        }]
      }
    } else {
      var painterQuestions = {
        saved: false,
        ...painterPartialData,
      } as PainterQuestionsModel;
      signupModel = {
        painterQuestions: [painterQuestions]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static updateSellerQuestions(sellerPartialData: any): SignupModel {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel;
    if (signup) {
      signupModel = JSON.parse(signup);

      if (signupModel.sellerQuestions) {
        const latestQuestion = signupModel.sellerQuestions.filter(sellerQuestion => sellerQuestion.saved === false);
        if (latestQuestion.length === 1) {
          let idx = signupModel.sellerQuestions.indexOf(latestQuestion[0]);
          signupModel.sellerQuestions[idx] = {
            ...signupModel.sellerQuestions[idx],
            ...sellerPartialData
          };
        } else {
          signupModel.sellerQuestions.push({
            saved: false,
            ...sellerPartialData,
          });
        }

      } else {
        signupModel.sellerQuestions = [{
          saved: false,
          ...sellerPartialData,
        }]
      }
    } else {
      var sellerQuestions = {
        saved: false,
        ...sellerPartialData,
      } as SellerQuestionsModel;
      signupModel = {
        sellerQuestions: [sellerQuestions]
      } as SignupModel;
    }
    localStorage.setItem('signup', JSON.stringify(signupModel));
    return signupModel;
  }

  static get(): SignupModel | null {
    const signup = localStorage.getItem('signup');
    let signupModel: SignupModel | null = null;
    if (signup) {
      signupModel = JSON.parse(signup);
    }
    return signupModel;
  }

  static toAmarelinhoExperiencesDto(model: SignupModel): ExperienceDto[] {
    function isValidExperience(xp: JobExperiencesModel): boolean {
      return !!(xp.position && xp.activities && xp.companyName && xp.saved === true && xp.start);
    }

    const experiences = (model.experiences ?? []).filter(ex => isValidExperience(ex)).map(obj => {
      const dtStart = new Date(obj.start);
      const dataAdmissao = dtStart.toISOString();

      let dataDesligamento;
      if (obj.finish) {
        const dtFinish = new Date(obj.finish);
        dataDesligamento = dtFinish.toISOString();
      } else {
        dataDesligamento = null;
      }

      const experience: ExperienceDto = {
        assignments: obj.activities,
        role: obj.position,
        startDate: dataAdmissao,
        endDate: dataDesligamento,
        company: obj.companyName
      };

      return experience;
    });

    if (model.cvExtraData) {
      const extraDataDate = new Date('2000-01-01').toISOString();
      experiences.push({
        id: 0,
        assignments: JSON.stringify(model.cvExtraData),
        role: CV_EXTRA_DATA_EXPERIENCE_KEY,
        startDate: extraDataDate,
        endDate: extraDataDate,
        company: CV_EXTRA_DATA_EXPERIENCE_KEY,
        lastSalary: null
      });
    }

    return experiences;
  }

  static toAmarelinhoSchoolingListDto(model: SignupModel): SchoolingDto[] {
    function isValidGraduation(graduation: GraduationModel): boolean {
      return !!(graduation.name && graduation.saved && graduation.start);
    }

    return model.graduations.filter(graduation => isValidGraduation(graduation)).map(graduation => {
      let dtStart = new Date(graduation.start);

      const dataInicio = dtStart.toISOString();

      let dataConclusao;
      if (graduation.finish) {
        const dtFinish = new Date(graduation.finish);
        dataConclusao = dtFinish.toISOString();
      } else {
        dataConclusao = null;
      }


      const schooling: SchoolingDto = {
        level: toSchoolingLevel(graduation.type),
        course: graduation.course,
        institution: graduation.name,
        startDate: dataInicio,
        endDate: dataConclusao,
      };

      return schooling;
    })
  }

  static toAmarelinhoCoursesDto(model: SignupModel): CourseDto[] {
    function isValidCourse(course: CourseModel): boolean {
      return !!(course.name && course.saved && course.start);
    }

    return model.courses.filter(course => isValidCourse(course)).map(course => {
      const dtStart = new Date(course.start);
      const dataInicio = dtStart.toISOString();

      let dataConclusao;
      if (course.finish) {
        const dtFinish = new Date(course.finish);
        dataConclusao = dtFinish.toISOString();
      } else {
        dataConclusao = null;
      }

      if (course.type === CourseType.OUTRO) {
        course.course = `${course.typeOther} - ${course.course}`
      }

      const courseDto: CourseDto = {
        type: getCourseType(course.type),
        name: course.course,
        institution: course.name,
        startDate: dataInicio,
        endDate: dataConclusao
      };

      return courseDto;
    })
  }

  static toAmarelinhoLanguageDto(model: SignupModel): LanguageDto[] {
    function isValidLanguage(language: LanguageModel): boolean {
      return language.level !== null && language.type !== null && language.saved === true;
    }

    function getLanguageType(type: LanguageType): LanguageNameDto {
      switch (type) {
        case LanguageType.ALEMAO: return 'Alemão';
        case LanguageType.ESPANHOL: return 'Espanhol';
        case LanguageType.FRANCES: return 'Francês';
        case LanguageType.INGLES: return 'Inglês';
        case LanguageType.OUTRO: return 'Português';
      }
    }

    function getLanguageLevel(level: LanguageLevel): LanguageLevelDto {
      switch (level) {
        case LanguageLevel.BASICO: return 'Básico';
        case LanguageLevel.FLUENTE: return 'Fluente';
        case LanguageLevel.INTERMEDIARIO: return 'Intermediário';
        case LanguageLevel.NATIVO: return 'Nativo';
      }
    }

    return model.languages.filter(language => isValidLanguage(language)).map(language => {
      const languageDto: LanguageDto = {
        name: getLanguageType(language.type),
        level: getLanguageLevel(language.level),
      };

      return languageDto;
    });
  }

  static toAmarelinhoDesiredPositionsDto(model: SignupModel): string[] {
    return model.objectives.map(obj => obj.label);
  }

  static toAmarelinhoPresentationLetterDto(model: SignupModel): string {
    return model.presentationLetter.description;
  }

  static toSignUpDto(model: SignupModel): any {
    function maiusculaPrimeiraLetrar(valor: string): string {
      return valor[0].toUpperCase() + valor.slice(1);
    }

    function getSexo(value: string): string {
      let genderCode;
      switch (value) {
        case 'Feminino':
          genderCode = 'FE';
          break;
        case 'Masculino':
          genderCode = 'MA';
          break;
        default:
          genderCode = 'ND';
          break;
      }
      return genderCode;
    }

    function getComplement(value: string): string {
      let complement;
      switch (value) {
        case 'ND':
          complement = '';
          break;
        default:
          complement = value;
          break;
      }
      return complement;
    }

    return {
      name: maiusculaPrimeiraLetrar(model.name.trim().split(' ')[0]),
      lastName: maiusculaPrimeiraLetrar(model.name.trim().substring(model.name.trim().indexOf(' ') + 1)),
      cpf: model.document,
      birthdate: new Date(model.birthdate),
      phoneCode: model.phone.substring(0, 2),
      phoneNumber: model.phone.substring(2),
      address: {
        cep: model.address.zipCode,
        street: model.address.street,
        number: model.address.number,
        complement: getComplement(model.address.complement),
        district: model.address.neighborhood,
        city: model.address.city,
        state: model.address.state
      },
      gender: getSexo(model.gender),
      email: model.email,
      password: model.password,
      utm_medium: 'chatbot',
      utms: UtmService.getStoredUtmAttribution(),
    };
  }


  static toSurveyDriverQuestionModel(question: DriverQuestionsModel): any {

    return {
      externalId: SignupModelHelper.getUserID(),
      data: {
        workAtNight: question.workAtNight,
        workWeekend: question.workWeekend,
        license: question.license,
        vehicle: question.vehicle,
        vehicleTrunk: question.vehicleTrunk,
        vehicleCargo: question.vehicleCargo,
        vehicleMopp: question.vehicleMopp,
        vehicleYear: question.vehicleYear
      }
    };
  }

  static toSurveyDressmakerQuestionsModel(question: DressmakerQuestionsModel): any {

    return {
      externalId: SignupModelHelper.getUserID(),
      data: {
        machine: question.machine,
        cloth: question.cloth
      }
    };
  }

  static toSurveyManicureQuestionsModel(question: ManicureQuestionsModel): any {

    return {
      externalId: SignupModelHelper.getUserID(),
      data: {
        ownMaterial: question.ownMaterial,
        hasCompanyOpen: question.hasCompanyOpen,
        nailTypes: question.nailTypes,
      }
    };
  }

  static toSurveyPainterQuestionsModel(question: PainterQuestionsModel): any {

    return {
      externalId: SignupModelHelper.getUserID(),
      data: {
        technique: question.technique,
      }
    };
  }

  static toSurveySellerQuestionsModel(question: SellerQuestionsModel): any {

    return {
      externalId: SignupModelHelper.getUserID(),
      data: {
        saleType: question.saleType,
      }
    };
  }
}
